import { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import {
  type KmAttrType,
  type KmEntity,
  KM_NAME_ATTR_CODE,
  useKmAttrTypes,
  useKmEntityTypeAttrs,
} from '@/entities/ontology';
import { isKmFormFieldDirty, type KmAttrTypeResolver, parseKmAttrValue, toKmAttrFormValue } from './attrFormValue';

/** Размер выборки справочника атрибутов: он нужен целиком — из него берутся имена и типы */
const ATTR_TYPES_LIMIT = 1000;

/** Строка схемы типа: атрибут с его настройками и текущим значением экземпляра */
export type KmSchemaAttrRow = {
  code: string;
  /** Название атрибута из справочника; без записи в справочнике — сам код */
  name: string;
  /** Атрибут справочника: по нему выбирается способ ввода. Без него значение только показывается */
  attrType?: KmAttrType;
  isRequired: boolean;
  /** У экземпляра есть значение по этому коду */
  isFilled: boolean;
  /** Значение экземпляра — для строки, которой редактор построить не из чего */
  value: unknown;
};

/** Строка значения с кодом вне схемы типа: показывается, но не изменяется */
export type KmExtraAttrRow = {
  code: string;
  value: unknown;
};

/**
 * Строка значений атрибутов в форме карточки: по строке на каждый атрибут схемы,
 * в порядке отображения.
 *
 * Значение объявлено неопределённым по типу намеренно: его форма зависит от типа атрибута
 * и рекурсивна (структура из полей), а форма раскручивает пути своих значений по типу.
 */
export type KmAttrRowValue = { code: string; value: unknown };

/** Строки значений атрибутов в схеме формы карточки */
export const kmAttrRowsSchema = z.array(z.object({ code: z.string(), value: z.unknown() }));

/**
 * Проверка набранных значений по схеме типа: обязательность и приводимость к типу атрибута.
 * Отказ отмечается на самой строке, поэтому проверка идёт в схеме формы карточки.
 */
export const checkKmAttrRows = (
  schemaRows: KmSchemaAttrRow[],
  values: KmAttrRowValue[],
  resolveAttrType: KmAttrTypeResolver,
  t: TFunction<readonly ['kmEntities', 'common']>,
  ctx: z.RefinementCtx,
) => {
  values.forEach((row, index) => {
    const attrType = schemaRows[index]?.attrType;
    if (!attrType) {
      return;
    }

    const parsed = parseKmAttrValue(attrType, row.value, resolveAttrType);
    const path = ['rows', index, 'value'];

    if (parsed.error) {
      ctx.addIssue({ code: 'custom', message: t(`kmEntities:attrValueError.${parsed.error}`), path });
      return;
    }
    if (schemaRows[index].isRequired && parsed.value === null) {
      ctx.addIssue({ code: 'custom', message: t('common:required'), path });
    }
  });
};

/**
 * Изменённые значения строк — в значения экземпляра. Неизменённые в запрос не идут:
 * иначе одновременная правка соседнего значения затиралась бы.
 */
export const collectChangedKmAttrValues = (
  schemaRows: KmSchemaAttrRow[],
  values: KmAttrRowValue[],
  dirtyRows: unknown,
  resolveAttrType: KmAttrTypeResolver,
): Record<string, unknown> => {
  const dirtyList = Array.isArray(dirtyRows) ? dirtyRows : [];
  const changed: Record<string, unknown> = {};

  values.forEach((row, index) => {
    const attrType = schemaRows[index]?.attrType;
    const dirtyRow = dirtyList[index] as { value?: unknown } | undefined;
    if (!attrType || !isKmFormFieldDirty(dirtyRow?.value)) {
      return;
    }

    const parsed = parseKmAttrValue(attrType, row.value, resolveAttrType);
    if (!parsed.error) {
      changed[row.code] = parsed.value;
    }
  });

  return changed;
};

/**
 * Значения атрибутов экземпляра для формы карточки: строки строятся по схеме его типа,
 * значения с кодами вне схемы идут отдельным набором — они только показываются.
 */
export const useKmEntityAttrsForm = (entity: KmEntity) => {
  const { data: schemaAttrs } = useKmEntityTypeAttrs(entity.entityTypeCode);
  const { data: attrTypesPage } = useKmAttrTypes({ limit: ATTR_TYPES_LIMIT });

  const attrTypes = useMemo(
    () => new Map((attrTypesPage?.items ?? []).map((attrType) => [attrType.code, attrType])),
    [attrTypesPage],
  );

  const resolveAttrType = useCallback<KmAttrTypeResolver>((code) => attrTypes.get(code), [attrTypes]);

  // Название сущности правится заголовком карточки, поэтому строкой атрибута не идёт
  const schemaRows = useMemo<KmSchemaAttrRow[]>(
    () =>
      (schemaAttrs?.items ?? [])
        .filter((attr) => !attr.isDeleted && attr.attrTypeCode !== KM_NAME_ATTR_CODE)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map<KmSchemaAttrRow>((attr) => ({
          code: attr.attrTypeCode,
          name: attrTypes.get(attr.attrTypeCode)?.name ?? attr.attrTypeCode,
          attrType: attrTypes.get(attr.attrTypeCode),
          isRequired: attr.isRequired,
          isFilled: attr.attrTypeCode in entity.data && entity.data[attr.attrTypeCode] !== null,
          value: entity.data[attr.attrTypeCode],
        })),
    [schemaAttrs, attrTypes, entity.data],
  );

  const extraRows = useMemo<KmExtraAttrRow[]>(() => {
    // Схема берётся целиком, а не по строкам: у названия своей строки нет, но и «вне схемы» оно не значение
    const schemaCodes = new Set(
      (schemaAttrs?.items ?? []).filter((attr) => !attr.isDeleted).map((attr) => attr.attrTypeCode),
    );
    // Название живёт в заголовке карточки — среди значений его нет и тогда, когда схема типа его не предусматривает
    return Object.keys(entity.data)
      .filter((code) => code !== KM_NAME_ATTR_CODE && !schemaCodes.has(code))
      .map<KmExtraAttrRow>((code) => ({ code, value: entity.data[code] }));
  }, [schemaAttrs, entity.data]);

  const rowValues = useMemo<KmAttrRowValue[]>(
    () =>
      schemaRows.map((row) => ({
        code: row.code,
        value: row.attrType ? toKmAttrFormValue(row.attrType, entity.data[row.code], resolveAttrType) : '',
      })),
    [schemaRows, entity.data, resolveAttrType],
  );

  return { schemaRows, extraRows, rowValues, resolveAttrType } as const;
};
